import TextLabel from "../Controls/TextLabel.mjs";

/* Application-specific global library */

const G = window.glob;
const appLib = {};

const _status = {
	config: G.getStatus('CopioneConfig') || {},
	player: G.getStatus("Player"),
	players: G.getStatus("Players"),
	groups: G.getStatus("Groups"),
	game: G.getStatus('Game'),
	chosen : G.getStatus("GameChosen"),
	scenario: G.getStatus('Scenario'),
	roles : G.getStatus('Copione')?.role,
	/** @type {Record<string,IncomingIm>} */
	unreadIm : {}
};

const allGroups = () => [].concat(_status.groups).filter( g => !! g?.id );
const hasGlobalGroup = () => !! allGroups().find(g => g?.id === "*")?.id;

G.register( 'Player', x=>{
	_status.player = x;
	delete _status.status;
	G.setStatus('PlayerStatus');
});
G.register( 'Players', x=>{ _status.players = x; });
G.register( 'Groups', x=>{ _status.groups = x; });
G.register( 'Game', x=>{ _status.game = x; });
G.register( 'GameChosen', x=>{
	_status.chosen = x;
	delete _status.status;
	G.setStatus('PlayerStatus');
	appLib.getPlayerStatus();
});
G.register( 'Scenario', x=>{ _status.scenario = x; });
G.register( 'CopioneConfig', (x)=>{ _status.config = (x || {}); });
G.register( 'Copione', (x)=>{ _status.roles = (x?.role || []); });

G.register( 'GameStatus', (sdata)=>{
	const running = (sdata?.status === 'running');
	const appNode = G.prop.appRootNode.querySelector('.App');
	appNode.classList.toggle('gameRunning', running);
	appNode.classList.toggle('gameNotRunning', ! running);
});

const isValidPlayerStatus = (s) => (!! ( s && typeof s === 'object' && typeof s.error === 'undefined' ));

/**
 * @function
 * @param {string} [key]
 * @return {Promise<any>}
 */
appLib.getPlayerStatus = ( key = '' ) => {
	return new Promise( ( resolve, reject ) => {
		if ( ! _status.chosen?.id ) return resolve({});
		if ( isValidPlayerStatus(_status.status) ) {
			const ret = key.length ? _status.status[key] : _status.status;
			return resolve( G.clone( ret ) );
		}
		G.api( { entity: 'playerstatus', operation: 'select' } ).then( s => {
			if ( isValidPlayerStatus(s) ) {
				if ( s?.read?.group ) {
					if ( ! s.read?.im ) s.read.im = {};
					Object.keys( s.read?.group ).forEach( ( k ) => {
						s.read.im['group:'+k] = s.read?.group[k];
					});
					delete s.read.group;
				}
				_status.status = s
				G.setStatus('PlayerStatus',s);
				const ret = key.length ? _status.status[key] : _status.status;
				return resolve( G.clone( ret ) );
			}
			reject(s);
		}).catch( e => {
			reject( e );
		});
	});
};

/**
 * Doesn't query online, can return undefined for whole status
 * @function
 * @param {string} [key]
 * @return {any}
 */
appLib.curPlayerStatus = ( key = '' ) => {
	if ( ! _status.chosen?.id ) return undefined;
	if ( isValidPlayerStatus(_status.status) ) {
		const ret = key.length ? _status.status[key] : _status.status;
		return G.clone( ret );
	}
	return undefined;
};

/**
 * Doesn't query online, if whole status is undefined return {}
 * @function
 * @param {string} [key]
 * @return {any}
 */
appLib.currentPlayerStatus = (key='') => key ? appLib.curPlayerStatus(key) : (appLib.curPlayerStatus() || {});

/**
 * Set the whole status or a key, triggers status commit, returns the whole status
 * @function
 * @param {any} status Needs an object if the whole status is going to be set
 * @param {string} [key]
 * @return {Promise<Object>}
 */
appLib.setPlayerStatus = ( status, key='' ) => {
	return new Promise( async ( resolve, reject ) => {
		if ( ! _status.chosen?.id ) return resolve({});
		if ( key.length ) {
			const s = await appLib.getPlayerStatus();
			s[key] = status;
			status = s;
		}
		if ( ! isValidPlayerStatus(status) ) {
			return reject( new Error( 'Invalid Status' ) );
		}
		G.setStatus('PlayerStatus',_status.status = status );
		if ( ! _status.commitStatusTimeout ) {
			_status.commitStatusTimeout = window.setTimeout( commitPlayerStatus, 5000);
		}
		resolve(status);
	});
};

/**
 * Commit on server user's status
 * @function
 * @return {Promise<void>}
 */
const commitPlayerStatus = () => {
	if ( _status.commitStatusTimeout ) {
		try { window.clearTimeout( _status.commitStatusTimeout ); } catch (e) { }
		delete _status.commitStatusTimeout;
	}
	const s = G.clone( _status.status );
	if (! isValidPlayerStatus(s) ) return;
	const g = {};
	Object.keys( s?.read?.im ?? [] )
		.filter( k => k.startsWith('group:'))
		.forEach( k =>{
			g[k.replace(/^group:/, '')] = s.read.im[k];
			delete s.read.im[k];
		});
	if ( Object.keys(g).length ) s.read.group = g;
	return G.api({ entity: 'playerstatus', operation: 'insert' }, s );
};

/**
 * @function
 * @async
 * @param {IncomingMail|string} m Mail message or mail message ID
 * @return {Promise<void>}
 */
appLib.setMailRead = async ( m ) => {
	if ( typeof m === "object" ) m = m.id;
	const r = (await appLib.getPlayerStatus('read')) || {};
	if ( ! r.mail ) r.mail = {};
	if ( ! r.mail[m] ) {
		r.mail[m] = (new Date()).getTime();
		await appLib.setPlayerStatus(r, 'read');
		G.setStatus("UnreadMailCount", (G.getStatus('UnreadMailCount')||1) - 1);
		G.trigger('ForceCheckReceived',true);
	}
};

/**
 * @function
 * @async
 * @param {IncomingMail|string} m Mail message or mail message ID
 * @return {Promise<void>}
 */
appLib.setMailUnread = async ( m ) => {
	if ( typeof m === "object" ) m = m.id;
	const r = (await appLib.getPlayerStatus('read')) || {};
	if ( ! r.mail ) r.mail = {};
	if ( r.mail[m] ) {
		delete r.mail[m];
		await appLib.setPlayerStatus(r, 'read');
		G.setStatus("UnreadMailCount", (G.getStatus('UnreadMailCount')||0) + 1);
		G.trigger('ForceCheckReceived',true);
	}
};

/**
 * @function
 * @async
 * @param {IncomingIm|string} m Instant message or instant message ID
 * @return {Promise<void>}
 */
appLib.setImRead = async ( m ) => {
	if ( typeof m === "object" ) m = m.id;
	if ( ! _status.unreadIm[m] ) return;
	delete _status.unreadIm[m];
	const r = (await appLib.getPlayerStatus('read')) || {};
	if ( ! r.im ) r.im = {};
	r.im[m] = (new Date()).getTime();
	await appLib.setPlayerStatus(r, 'read');
	G.setStatus('UnreadImCount', Object.keys(_status.unreadIm).length);
};

/**
 * @function
 * @return {Record<string,IncomingIm>}
 */
appLib.getImUnread = () => G.clone(_status.unreadIm);

const _sounds = {
	'error': { name: 'error.mp4'},
	'email' : { name: 'mail.mp4' },
	'im' : { name: 'message.mp4' },
	'sent' : { name: 'sent.mp4' }
};

/**
 * @function
 * @async
 * @return {Promise<boolean>}
 */
appLib.isSoundEnabled = async () => {
	let en = await appLib.getPlayerStatus('notificationsound');
	if ( typeof en === 'undefined') en = ! appLib.getConfigOption("notificationsound").disabled;
	return en;
};

/**
 * Play a sound (by name)
 * @param {string} soundName
 * @return {Promise<void>}
 */
appLib.sound = async (soundName = '') => {
	if ( ! (soundName && _sounds[soundName]) ) return;
	if (! ( await appLib.isSoundEnabled() ) ) return;
	if ( typeof  _sounds[soundName].sound === 'undefined') {
		_sounds[soundName].sound = new Audio(window.glob.getAppWebRoot() + "sounds/" + _sounds[soundName].name );
	}
	await _sounds[soundName].sound.play().catch((error) => {
		console.error("Errore nel riprodurre il suono", error);
	});
};

appLib.getConfigOption = ( configEntryName ) => {
	const c = _status.config[configEntryName];
	if ( typeof c === 'undefined' ) return { disabled: true, undefined: true };
	return G.clone( c );
};

/**
 * @function
 * @async
 * @return {Promise<boolean>}
 */
appLib.refreshAllData = async () => {
	const player = _status.player;
	const appinfo = await G.api(
		{entity: "startup", operation: "appinfo"},
		{appid: G.prop.appID}
	);
	let players, scenario = _status.scenario;
	if (scenario) {
		scenario = appinfo.scenari.find((s) => s.id === scenario.id);
		players = await G.api(
			{entity: "startup", operation: "listplayers"},
			{domain: appinfo.domain, scenario: scenario.id}
		);
	}
	if (scenario && player) await appLib.refreshGameData();
	G.setStatus("AppInfo", appinfo, true);
	G.setStatus("Players", players?.players, true);
	G.setStatus("Scenario", scenario, true);
	return true;
};

/**
 * @function
 * @async
 * @return {Promise<void>}
 */
appLib.refreshGameData = async () => {
	const player = _status.player;
	const scenario = _status.scenario;
	let gamestatus, gamechosen = _status.chosen;
	if (gamechosen) {
		const feedName = player.admin ? scenario.feedAdmin : scenario.feed;
		let si = await G.getFeed(feedName);
		gamechosen = (si?.games || []).find((g) => g.id === gamechosen.id);
	}
	if (gamechosen?.feedStatus) {
		const feedName = gamechosen.feedStatus;
		if (G.prop.polling[feedName]) {
			gamestatus = await G.processPoll(feedName);
		} else {
			gamestatus = await G.getFeed(feedName);
		}
	}
	G.setStatus("Game", undefined);
	G.setStatus("GameStatus", gamestatus);
	G.setStatus("GameChosen", gamechosen, true);
};

/**
 * @function
 * @async
 * @param {boolean} force
 * @return {never}
 */
appLib.logout = async (force) => {
	if (!force && !window.confirm(G.label("ConfirmLogout"))) return;
	// G.setStatus("GameChosen", undefined, true);
	// G.setStatus("GameStatus", undefined);
	// G.setStatus("Game", undefined);
	// G.setStatus("Scenario", undefined, true);
	// G.setStatus("Copione", undefined, true);
	// G.setStatus("Players", undefined, true);
	// G.setStatus("Player", undefined, true);
	// G.setStatus("AppInfo", undefined, true);
	G.logout(true);
};

/**
 * @function
 * @async
 * @return {Promise<boolean>}
 */
appLib.closeGame = async () => {
	G.setStatus("GameChosen", undefined, true);
	G.setStatus("GameStatus", undefined);
	G.setStatus("Game", undefined);
	G.setCurrentPageParam("tab", "Dashboard");
	return await appLib.refreshAllData();
};

/**
 * @function
 * @param {string} roleId
 * @param {string} [playerId]
 * @return {string}
 */
appLib.nameByRole = (roleId, playerId) => {
	const ps = _status.players || [];
	let out;
	if (playerId) {
		const pl = ps.find((p) => p.id === playerId);
		if (pl) out = (pl?.roles || []).find((r) => r.id === roleId)?.name;
		if (out) return out;
	}
	out = _status.roles.find( r => (r.id === roleId))?.name;
	return out ?? roleId;
};

appLib.role2player = appLib.nameByRole;

/**
 * @function
 * @param {string} playerId
 * @return {string}
 */
appLib.player2name = (playerId) => {
	const p = (_status.players || []).find(p => (p?.id === playerId));
	return p ? p.name : playerId;
};

/**
 * @function
 * @param {string} playerId
 * @return {string}
 */
appLib.player2icon = (playerId) => {
	const p = (_status.players || []).find(p => (p?.id === playerId));
	return p?.icon;
};

appLib.getGroup = (groupId) => {
	const p = allGroups().find(p => (p?.id === groupId));
	return p ? G.clone(p) : undefined;
};

appLib.group2name = (groupId) => appLib.getGroup(groupId)?.name ?? groupId;

appLib.group2icon = (groupId) => appLib.getGroup(groupId)?.icon;

appLib.mailByPlayer = (playerId) => {
	const p = playerId
		? _status.players.find((p) => p.id === playerId)
		: _status.player;
	const read = appLib.currentPlayerStatus('read')?.mail || {};
	const out = {
		lastupdate: { inbox: 0, sent: 0 },
		unread: 0,
		data: {inbox: [], sent: [] }
	};
	if (!p || !p?.roles) return out;
	playerId = p.id;
	const all = (_status.game || {})?.mail || [];
	const recipients = (m) => {
		let out = [];
		if ( m.to ) out = out.concat( Array.isArray(m.to) ? m.to : [m.to] );
		if ( m.cc ) out = out.concat( Array.isArray(m.cc) ? m.cc : [m.cc] );
		if ( m.bcc ) out = out.concat( Array.isArray(m.bcc) ? m.bcc : [m.bcc] );
		return out;
	};
	all.forEach( m =>{
		const recs = recipients(m);
		if ( recs.find( t => p.roles.find( pr => pr.id === t) )) {
			if ( m.time > out.lastupdate.inbox )
				out.lastupdate.inbox = m.time;
			if ( ! read[m.id] ) out.unread++;
			out.data.inbox.unshift( m );
		}
		if ( m.player === p.id ) {
			if ( m.time > out.lastupdate.sent )
				out.lastupdate.sent = m.time;
			out.data.sent.unshift( m );
		}
	});
	G.setStatus("UnreadMailCount", out.unread);
	return out;
};

appLib.imByPlayer = (playerId) => {
	const p = playerId
		? _status.players.find((p) => p.id === playerId)
		: _status.player;
	const out = {
		lastupdate: {},
		data: {},
		unread: {},
		lastreceived: {}
	};
	const read = appLib.currentPlayerStatus('read')?.im || {};
	if (!p || !p?.roles) return out;
	playerId = p.id;
	const playerGroups = appLib.chatGroups( p.id ).map( g => g.id );
	const all = (_status.game || {})?.im || [];
	all.forEach( m => {
		const sentFlag = (m.player === playerId );
		let k = false;
		if ( sentFlag ) {
			k = (m.group && 'group:'+m.group) || (m.to && m.to[0]) || false;
		} else if ( m.to && m.to.includes(playerId) ) {
			k = m.player;
		} else if ( m.group && playerGroups.includes(m.group) ) {
			k = 'group:'+m.group;
		}
		if ( ! k ) return;
		const lu = out.lastupdate[k] || 0;
		if ( m.time > lu ) {
			out.lastupdate[k] = m.time;
			if ( ! sentFlag ) out.lastreceived[k] = m.time;
		}
		if ( ! out.data[k] ) out.data[k] = [];
		out.data[k].push( m );
	});
	Object.keys( out.data ).forEach( k => {
		if ( out.lastreceived[k] > ( read[k] || 0 ) ) {
			out.unread[k] = out.lastupdate[k];
		}
	});
	out.data = G.clone( out.data );
	if ( hasGlobalGroup() && ! out.data['group:*'] ) out.data['group:*'] = [];
	_status.unreadIm = G.clone(out.unread);
	G.setStatus("UnreadImCount", Object.keys(out.unread).length);
	return out;
};

const myThrow = ( txt, data = {} ) => {
	console.error( txt, data );
	throw (new Error( txt ));
};

const htmlTagRegex = new RegExp('<[a-z]+>','i');
const mailRecipientsFields = String('to cc bcc').split(' ' );
const mailAllowedFields = String('to group from cc bcc subject body html text thread reply body attachments').split(' ' );
const attachAllowedFields = String('id name content size type').split(' ');

const filterRecipients = ( mailData = {} ) => {
	const roles = {};
	(_status.players || [] ).forEach(( p ) => {
		roles[p.id] = true;
		(p.roles || []).forEach( r => {
			if ( r?.id ) roles[r.id] = true;
		})
	});
	mailRecipientsFields.forEach( rf => {
		let data = mailData[rf];
		if ( ! data ) data = [];
		if ( typeof data === "string") data = [data];
		if ( ! Array.isArray(data)) data = [];
		data = data.filter( i => ( i && typeof i === 'string' && i.length && (roles[i]||i.includes('@')) ));
		if (  data.length ) {
			mailData[rf] = data;
		} else {
			delete mailData[rf];
		}
	});
	return mailData;
};
const filterAttachments = ( mailData = {} ) => {
	let attachments = mailData.attachments;
	if ( ! attachments ) attachments = [];
	if ( ! Array.isArray(attachments) ) attachments = [attachments];
	attachments = attachments.filter( a => {
		if ( ! a || typeof a !== 'object' ) return false;
		if ( ! a.name || typeof a.name !== 'string') {
			myThrow('attachments', {message: 'Invalid attach name', data: a});
			return false;
		}
		if ( a.id ) {
			delete a.content;
		} else  if ( ! a.content || typeof a.content !== 'string') {
			myThrow('attachments', { message: 'Invalid attach content', data:  a});
			return false;
		}
		return true;
	});
	if ( attachments.length ) {
		attachments.forEach( a => {
			Object.keys(a).forEach( k => {
				if ( ! attachAllowedFields.includes(k) ) delete a[k];
			})
		});
		mailData.attachments = attachments;
	} else {
		delete mailData.attachments;
	}
	return mailData;
};
const baseCheckCommunication = (data = {}, localThrow = myThrow ) => {
	const p = _status.player;
	if ( ! p ) return localThrow('player', { message: 'Not logged in' } );
	const ps = _status.players;
	const inData = G.clone(data);
	if ( ! ps ) return localThrow('players',{message: 'Cannot send at this stage (players unkown)' });
	if ( ! _status.game ) return localThrow('game', { message: 'Cannot send at this stage (game not found)' });
	if ( ! ( p?.roles || []).length ) return localThrow('player',{ message: 'User without roles', data: p});
	if (typeof data !== 'object' ) return localThrow('data', { message: 'Parameter mus be an object',data: inData});
	Object.keys( data ).forEach( k =>{
		if ( mailAllowedFields.includes(k) ) return;
		console.warn('Unforeseen key in message: '+k,'warn');
		console.warn(inData,'warn');
		delete data[k];
	});
	data = filterRecipients( data );
	if ( data.group ) {
		if (typeof data.group !== 'string') return localThrow('group',{ message: '"group" must be a string (valid group ID)', data: inData});
	} else {
		if ( ! data?.to ) return localThrow('to',{ message: '"to" must be a string (valid recipient) or an array of strings', data: inData});
	}
	// if ( typeof data.from === "string" && data.from.length ) {
	// 	if ( ! p.roles.find( r => ( r && r.id === data.from )) )
	// 		return localThrow('for',{ message: `"for" isn't in player's roles`,data: inData});
	// } else {
	// 	data.from = p.roles[0].id;
	// }
	if ( ! data.body ) data.body = '';
	if ( typeof data.body !== 'string')
		return localThrow('body', { message: 'Invalid "body"', data: inData});
	if ( ! data.html ) delete data.html;
	if ( ! data.text ) delete data.text;
	if ( ! data.html && ! data.text && data.body.match(htmlTagRegex)) data.html = true;
	if ( typeof data.attachments !== 'undefined' ) data = filterAttachments( data );
	return data;
}

appLib.checkMailData = ( data ) => {
	let error = false;
	try {
		const out = baseCheckCommunication( G.clone(data), (...args)=>{
			console.log(args);
			error = true;
		});
		if ( ! out?.subject || typeof out?.subject !== 'string') error = true;
	} catch(e) {
		console.log(e);
		error = true;
	}
	return ! error;
};

appLib.sendMail = async ( maildata = {} ) => {
	try {
		if ( typeof maildata?.from !== 'string' || ! maildata?.from ) myThrow('from', { message:'Wrong from', data: maildata });
		if ( typeof maildata?.subject !== 'string' || ! maildata?.subject ) myThrow('subject', { message:'Wrong subject', data: maildata });
		maildata = baseCheckCommunication(maildata);
	} catch (e) {
		return { error: e, field: e.message };
	}
	return (await G.api({entity:'game',operation:'mail'},maildata));
};

appLib.sendIm = async ( imdata = {} ) => {
	imdata = baseCheckCommunication(imdata);
	delete imdata.subject;
	delete imdata.attach;
	return (await G.api({entity:'game',operation:'im'},imdata));
};

appLib.rolesObject = ( includeMe = false ) => {
	const p = _status.player;
	const ps = _status.players;
	const out = {};
	if ( ! ( p && ps && Array.isArray(ps)) ) return out;
	ps.filter( tp => Array.isArray( tp?.roles )).forEach( t => {
		if ( t.id === p.id && ! includeMe ) return;
		t.roles.forEach( r => {
			if ( ! out[r.id] ) out[r.id] = r.name;
		});
	});
	return out;
};

appLib.getAttachObject = async ( id ) => {
	return (await G.api({entity:'game',operation:'attach'}, { id: id }));
};

appLib.downloadAttach = async (id) => {
	const attachment = await appLib.getAttachObject(id);
	const base64Data = attachment.content.replace(/^[^;]+;base64,/,'');
	const blobData = base64Data ? window.atob(base64Data) : "";
	const byteNumbers = new Array(blobData.length);
	for (let i = 0; i < blobData.length; i++) byteNumbers[i] = blobData.charCodeAt(i);
	const byteArray = new Uint8Array(byteNumbers);
	const blob = new Blob([byteArray], { type: attachment.type });
	const blobUrl = URL.createObjectURL(blob);
	const downloadLink = window.document.createElement("a");
	downloadLink.href = blobUrl;
	downloadLink.download = attachment.name;
	window.document.body.appendChild(downloadLink);
	downloadLink.click();
	window.document.body.removeChild(downloadLink);
	URL.revokeObjectURL(blobUrl);
};

appLib.smartSize = ( sizeInBytes ) => {
	if ( typeof sizeInBytes === 'string' ) sizeInBytes = parseInt(sizeInBytes);
	if ( typeof sizeInBytes !== 'number' || isNaN(sizeInBytes) ) return '';
	if ( sizeInBytes > 1000000000 ) return ( String(Math.round( sizeInBytes / 100000000 ) / 10)+' GB');
	if ( sizeInBytes > 1000000 ) return ( String(Math.round( sizeInBytes / 100000 ) / 10)+' MB');
	if ( sizeInBytes > 1000 ) return ( String(Math.round( sizeInBytes / 100 ) / 10)+' KB');
	return String(sizeInBytes)+' B';
};

appLib.attachLink = ( attach ) => {
	if ( ! attach?.id ) return '';
	return (
		<span
			key={attach.id}
			className={"attach"}
			onClick={ ( e )=>{
				e.stopPropagation();
				e.preventDefault();
				appLib.downloadAttach( attach.id )
			}}
			title={`${G.label('Download')} (${appLib.smartSize( attach.size )})` }
		>
			<TextLabel value={"attach"} lang={"icon"} />
			<b>{ attach.name }</b>
		</span>
	);
};

appLib.timer2current = ( timer ) => {
	return (((new Date().getTime()) - timer.time) + timer.timer);
};

appLib.timer = ( event ={}, lastupdate) => {
	let timer = undefined;
	const delay = event?.relativeDelay;
	if ( typeof delay === 'undefined') return timer;
	if ( typeof lastupdate === 'undefined' ) lastupdate = event.time;
	else if ( lastupdate?.lastupdate ) lastupdate = lastupdate.lastupdate;
	timer = { time: (new Date()).getTime() };
	const delta = timer.time - lastupdate;
	timer.timer = delay + delta - delay;
	return timer;
};

appLib.timer2string = ( timer ) => {
	return appLib.msec2string( appLib.timer2current( timer ) );
};

appLib.msec2string = ( msec ) => {
	if ( typeof msec !== 'number' ) return "";
	let secs = Math.floor(msec / 1000);
	const hs = Math.floor(secs / 3600);
	secs = secs % 3600;
	const ms = Math.floor(secs / 60);
	if ( hs ) return String(hs).padStart(2,'0')+":"+String(ms).padStart(2,'0')+'’';
	return String(ms)+'’';
};

appLib.replySubject = ( originaSubject = "") => ("Re: "+String(originaSubject).replace(/^ *Re? *: */i,''));

appLib.chatGroups = ( playerId ) => {
	if ( typeof playerId === 'undefined' ) playerId = _status.player.id;
	return G.clone( allGroups().filter( g => g.players.find( gp => (gp.id === playerId ))));
};

const quoteForMail = t => {
	if ( typeof t !== 'string' ) return t;
	return t.replace(/(<blockquote>|<p>)+/g, '<blockquote>').replace(/(<\/blockquote>|<\/p>)+/g, '</blockquote>');
}

appLib.reply = ( orig = {}, toAll = false ) => {
	if ( ! orig?.id ) return myThrow('id', { message: "Reply to non valid mail (missing id)", data:  orig } );
	const p = _status.player;
	const lang = G.getLanguageLong() ?? 'it-IT';
	const myRoles = [];
	(p.roles || []).forEach( r => { myRoles.push( r.id )} );
	if ( myRoles.length === 0 ) return myThrow('player', { message: 'User without roles, cannot send mail', data: p } );
	const prolog = G.label('prologRe',{
		time:(new Date(orig.time)).toLocaleString(lang),
		user:appLib.nameByRole(orig.from, orig.player)
	});
	let from;
	mailRecipientsFields.forEach( f => {
		if ( from ) return;
		(orig[f] || []).forEach( o => {
			if ( from ) return;
			if ( myRoles.includes(o)) from = o;
		});
	});
	if ( ! from ) from = myRoles[0];
	const r = {
		from: from,
		to: [orig.from],
		reply: orig.id,
		thread: (orig.thread ?? orig.id),
		html: true,
		subject: appLib.replySubject( orig.subject ),
		body : `<p> </p><p>${ prolog }</p>`
	};
	if ( orig.text ) {
		r.body += '<blockquote>'+orig.body.replace(/\n*$/,'').replace(/\n/g,'<br />')+'</blockquote>';
	} else {
		r.body += quoteForMail(orig.body);
	}
	if ( toAll ) {
		const allRecipients = [orig.from, r.from ];
		mailRecipientsFields.filter( f => (f !== "bcc")).forEach( f => {
			if ( ! r[f] ) r[f] = [];
			(orig[f] || []).forEach( o => {
				if ( allRecipients.includes(o) ) return;
				if ( myRoles.includes(o) ) return;
				allRecipients.push(o);
				r[f].push(o);
			});
		});
	}
	return r;
};

appLib.qtAnagrafica = async () => {
	if ( typeof _status.qtAnagrafica !== 'number' ) {
		_status.qtAnagrafica = (await G.api({entity:'admin', operation:'count-anagrafica'}))?.entries;
	}
	return _status.qtAnagrafica;
}

appLib.hasAnagrafica = async () => ((await appLib.qtAnagrafica()) > 0);

appLib.forward = ( orig = {} ) => {
	if ( ! orig?.id ) return myThrow('id',{message: "Forward of non valid mail (missing id)", data: orig } );
	const p = _status.player;
	const lang = G.getLanguageLong() ?? 'it-IT';
	const myRoles = [];
	(p.roles || []).forEach( r => { myRoles.push( r.id )} );
	if ( myRoles.length === 0 ) return myThrow('player', {message: 'User without roles, cannot send mail', data: p} );
	let from;
	mailRecipientsFields.forEach( f => {
		if ( from ) return;
		(orig[f] || []).forEach( o => {
			if ( from ) return;
			if ( myRoles.includes(o)) from = o;
		});
	});
	if ( ! from ) from = myRoles[0];
	const r = {
		from: from,
		to: [],
		subject: "Fwd: "+String(orig.subject||'').replace(/^ *(I|Fwd?) *: */i,''),
		reply: orig.id,
		thread: (orig.thread ?? orig.id),
		html: true,
		body: '<p> </p><p>'+G.label("prologFwd")+'</p>'
	};
	if ( orig.attachments ) r.attachments = orig.attachments;
	const prebody = [];
	if ( orig.time ) prebody.push(G.label('Date')+': '+(new Date(orig.time)).toLocaleString(lang) );
	if ( orig.from ) prebody.push(G.label('From')+': «'+appLib.nameByRole(orig.from)+'»');
	if ( orig.to ) prebody.push(G.label('To')+': «'+orig.to.map( t => appLib.nameByRole(t) ).join('», «')+'»');
	if ( orig.cc ) prebody.push(G.label('CC')+': «'+orig.cc.map( t => appLib.nameByRole(t) ).join('», «')+'»');
	r.body += quoteForMail( '<p>'+prebody.join('<br />')+'</p>'+orig.body );
	// r.body += '<blockquote>'+
	// 	'<p>'+prebody.join('<br />')+'</p>'+
	// 	( orig.text ? '<p>'+orig.body.replace(/\n*$/,'').replace(/\n/g,'<br />')+'</p>' : orig.body ) +
	// 	'</blockquote>';
	return r;
};

appLib.isGameRunning = () => !! (G.prop.appRootNode && G.prop.appRootNode.querySelector(':scope > .App.gameRunning'));

appLib.userCanSendMail = () => !! (_status?.player?.roles && _status.player.roles.length);

appLib.toolbarOptions = () => [
	["bold", "italic", "underline"],
	["blockquote"],
	[{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
	["clean"],
];

window.AL = appLib;

export default appLib;
